import { useCallback } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { useNavigation } from "@react-navigation/native";
import { capitalizeFirstLetter, formatCurrency } from "../utils";
import { useDateFormatter, useIntervalFormatter } from "../hooks/date";
import { useI18n } from "../hooks/i18n-hooks";
import { Invoice } from "../__generated__/graphql";
import ListItem from "../ui_atoms/ListItem";
import { RootStackParamList } from "../navigations/RootNavigation";

type ScreenParams = StackScreenProps<RootStackParamList>;

type InvoiceItemProps = {
    invoice: Invoice;
};

const InvoiceItem = ({ invoice }: InvoiceItemProps) => {
    const i18n = useI18n();

    const navigator = useNavigation<ScreenParams["navigation"]>();

    const intlFormat = useDateFormatter();
    const intervalFormatter = useIntervalFormatter();

    const showInvoicePdf = useCallback(
        (invoice: Invoice) => {
            navigator.navigate("InvoiceDetails", {
                invoiceId: invoice.id,
            });
        },
        [navigator],
    );

    const isRefund = invoice.totalAmount < 0;

    return (
        <ListItem
            title={`${capitalizeFirstLetter(
                // we use to date as from date can be in the previous month
                intlFormat(new Date(invoice.toDate), {
                    month: "long",
                    year: "numeric",
                }),
            )} - ${i18n.t(`screens.user.invoices.${invoice.invoiceType}`)}`}
            description={[
                invoice.missionInfo.workerInfo?.name,
                invoice.missionInfo.name,
                intervalFormatter(new Date(invoice.fromDate), new Date(invoice.toDate)),
                formatCurrency({ amountInCents: invoice.totalAmount }),
            ].join("\n\n")}
            tags={[
                invoice.sentAt
                    ? capitalizeFirstLetter(
                          intlFormat(new Date(invoice.sentAt), {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                          }),
                      )
                    : null,
                i18n.t("screens.user.invoices.invoice_token", {
                    token: invoice.invoiceNumber,
                }),
            ]}
            avatarPath={invoice.missionInfo.workerInfo?.avatarUrl}
            useBigAvatarOrIcon
            {...(invoice.paidAt
                ? {
                      labels: [
                          {
                              text: !isRefund
                                  ? i18n.t("screens.user.invoices.paid_status")
                                  : i18n.t("screens.user.invoices.refunded_status"),
                              mode: "success",
                          },
                      ],
                  }
                : {})}
            onPress={() => showInvoicePdf(invoice)}
        />
    );
};

export default InvoiceItem;
